import { useCallback, useMemo, useState } from 'react';
import { delay, makeFullAddress } from '@/utils/common';
import { makeClinicSlug } from '@/utils/shared';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { ClinicHeader } from '@/app/_components/widgets/listing/cardV3/ClinicHeader';
import { BookingActions } from '@/app/_components/widgets/listing/cardV3/BookingActions';
import { NoAvailabilitiesInfo } from '@/app/_components/widgets/listing/cardV3/NoAvailabilitiesInfo';
import { AlgoliaClinicV2, License, PaymentType, SpecialityChannel } from '@/types/algolia-types';
import { ELBadge } from '@davincihealthcare/elty-design-system-react';
import { HomeIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { AvailabilityModalV3 } from '@/app/_components/widgets/listing/cardV3/AvailabilityModalV3';
import { formatPrice, getCheckoutURL } from '@/utils/checkout';
import { SlotAvailability } from '@/app/_components/widgets/listing/types';
import { ROUTES } from '@/routes';
import { useRouter } from 'next/navigation';
import { ChangeSpecializationWrapper } from '@/app/_components/widgets/listing/cardV3/ChangeSpecializationWrapper';
import { fireAddToCartEvent, fireConfirmTimeSelectionEvent } from '@/utils/trackingGtm';
import { DavinciLogo } from '@/app/_components/widgets/listing/cardV3/DavinciLogo';
import { Speciality } from '@/app/_components/widgets/listing/cardV3/Speciality';
import { getDoctorName } from '@/utils/reservation';
import { PriceSection } from '@/app/_components/widgets/listing/cardV3/PriceSection';
import { CardAvailabilityInfo } from '@/app/_components/widgets/listing/cardV3/CardAvailabilityInfo';
import { getStartOfDayTime } from '@/app/_components/widgets/listing/_utils/time';
import { DEFAULT_DOC_ALIAS } from '@/costants';
import { trackAddToCart } from '@/utils/trackingMeta-pixelV2';
import { getCookieByName } from '@/utils/cookies';
import { usePatient } from '@/app/_hooks/usePatient';

export type AlgoliaCardProps = Pick<
  AlgoliaClinicV2,
  | 'license'
  | 'address'
  | 'cap'
  | 'city'
  | 'logoUrl'
  | 'name'
  | 'specializations'
  | 'domiciliaryCare'
  | 'ambulatoryId'
  | 'availablePayments'
  | 'slug'
  | 'objectID'
  | '_rankingInfo'
>;

type Props = {
  item: AlgoliaCardProps;
  showAllServicesEnabled?: boolean;
  showPhoneNumber?: boolean;
  showNoAvailabilitiesCallToAction?: boolean;
  originalDoctorId?: string;
  showDistance?: boolean;
};

const CardV3: React.FC<Props> = ({
  item,
  showAllServicesEnabled = true,
  showPhoneNumber = true,
  showNoAvailabilitiesCallToAction = true,
  showDistance = false,
  originalDoctorId,
}) => {
  const { patient } = usePatient();
  const router = useRouter();

  const isLicensed = item?.license && (item.license as License) !== License.None;
  const isDomicile = item.domiciliaryCare;

  const isOnline = useMemo(
    () => item.specializations.specialityChannels?.includes(SpecialityChannel.Online),
    [item.specializations.specialityChannels],
  );

  const distance = showDistance && isLicensed && !isDomicile && !isOnline ? (item?._rankingInfo?.geoDistance ?? null) : null;

  const hasOnsitePayment = useMemo(() => item.availablePayments?.includes(PaymentType.Onsite), [item.availablePayments]);
  const { label: specialityName, id: specialityId, hasDynamicPrice } = item.specializations;

  const selectedDoctor = useMemo(() => {
    const doctorList = [...(item.specializations.doctors ?? [])];

    if (originalDoctorId) {
      const foundDoctor = doctorList.find(doctor => doctor.id === originalDoctorId);

      if (foundDoctor) {
        return foundDoctor;
      }
    }
    //  return the first doctor with availability
    const sortedDoctors = doctorList.sort((a, b) => (a.nextAvailability ?? Infinity) - (b.nextAvailability ?? Infinity));
    return sortedDoctors[0];
  }, [item.specializations.doctors, originalDoctorId]);

  const cardDoctorName = useMemo(() => {
    if (item.specializations.doctors?.length && item.specializations.doctors.every(doctor => !doctor.isNameVisible)) {
      return DEFAULT_DOC_ALIAS;
    }

    const specialityNumberOfDoctors = item.specializations.doctors?.length ?? 0;

    return specialityNumberOfDoctors > 1 ? `${specialityNumberOfDoctors} professionisti sanitari` : getDoctorName(selectedDoctor);
  }, [selectedDoctor, item.specializations.doctors]);

  const price = selectedDoctor?.price ?? 0;
  const doctorId = selectedDoctor?.id;
  const hasAvailableSlots = !!selectedDoctor?.nextAvailability;
  const workgroupId = item.ambulatoryId;
  const isWelfare = useIsWelfare();
  const completeAddress = useMemo(() => makeFullAddress(item.address, item.cap, item.city) ?? '', [item.address, item.cap, item.city]);

  const allClinicServicesLink = useMemo(
    () => makeClinicSlug(item, specialityName, specialityId, doctorId),
    [item, specialityId, specialityName, doctorId],
  );
  const clinicLink = useMemo(() => makeClinicSlug(item), [item]);
  const [showAllServicesModal, setShowAllServicesModal] = useState(false);

  const [isAvailabilityModalOpenInfo, setIsAvailabilityModalOpenInfo] = useState<null | { expectedFirstDay: number | null }>(null);

  const openAvailabilityModal = ({ expectedFirstDay }: { expectedFirstDay: number | null }) => {
    setIsAvailabilityModalOpenInfo({ expectedFirstDay });
    try {
      const metaPixelProduct = {
        fbc: getCookieByName('_fbc') || '',
        fbp: getCookieByName('_fbp') || '',
        value: selectedDoctor?.price || 0,
        userEmail: patient?.emails && patient?.emails.length > 0 ? patient?.emails[0] : '',
        price: 0,
        service: item.specializations.id || '',
      };
      trackAddToCart(metaPixelProduct);
    } catch (error) {
      console.error(error);
    }

    fireAddToCartEvent(`${selectedDoctor?.price}`, {
      item_name: item?.specializations?.label,
      item_brand: item?.name,
      item_variant: item?.specializations?.label,
      item_category: item?.address ?? undefined,
      price: formatPrice({ amount: selectedDoctor?.price }),
      index: 0,
    });
  };

  const handleSubmit = useCallback(
    async (selectedSlot: SlotAvailability) => {
      if (!selectedSlot) return;
      fireConfirmTimeSelectionEvent(formatPrice({ amount: price }), {
        item_name: item?.specializations?.label,
        item_brand: item?.name,
        item_variant: item?.specializations?.label,
        item_category: item?.address ?? undefined,
        price: formatPrice({ amount: price }),
        index: 0,
      });
      const checkoutUrl = getCheckoutURL({
        pathname: ROUTES.CHECKOUT_RECAP.pathname,
        availabilitySlotId: selectedSlot.id,
      });

      router.push(checkoutUrl);
      // Wait for the checkout page to load
      await delay(3000);
    },
    [item, price, router],
  );

  return (
    <div className="overflow-x-hidden rounded-lg shadow-cardListing">
      {showAllServicesModal && (
        <ChangeSpecializationWrapper
          workgroupId={workgroupId}
          domiciliaryCare={isDomicile}
          onCancel={() => setShowAllServicesModal(false)}
          onNewSlot={slot => {
            setShowAllServicesModal(false);
            handleSubmit(slot);
          }}
        />
      )}

      {hasOnsitePayment && !isWelfare && (
        <div className="flex items-center justify-center gap-2 bg-brandGreen-200 px-6 py-2 text-sm font-semibold text-primary lg:flex-row-reverse lg:justify-start">
          <DavinciLogo />
          {<span>Prenota ora e paga in struttura</span>}
        </div>
      )}
      <div className={`grid grid-cols-1 gap-4 bg-neutral-surface pb-4 lg:grid-cols-2 lg:gap-5 lg:pb-6 `}>
        <div className="flex flex-col gap-4 px-4 pt-4 lg:min-w-[300px] lg:pt-6">
          <div className="flex flex-col">
            <ClinicHeader
              distance={distance}
              logoSrc={item.logoUrl}
              clinicLink={clinicLink}
              clinicName={item.name}
              isLicensed={isLicensed}
              isDomicile={isDomicile}
              completeAddress={completeAddress}
              allClinicServicesLink={showAllServicesEnabled ? allClinicServicesLink : undefined}
              onShowAllServices={showAllServicesEnabled ? () => setShowAllServicesModal(true) : undefined}
            />
          </div>
          <div className="-ml-4 h-0.5 w-[calc(100%+2rem)] bg-slate-100 lg:hidden" />

          <div className="flex flex-1 items-center justify-between gap-3">
            <div className="flex flex-col gap-1 overflow-hidden">
              <Speciality
                isDomicile={isDomicile}
                specialityName={specialityName}
                hasDescription={item.specializations.hasDescription}
                specialityId={specialityId ?? null}
                logoUrl={item.logoUrl}
                price={price}
                hasDynamicPrice={hasDynamicPrice}
                clinicName={item.name}
                clinicLink={clinicLink}
                completeAddress={completeAddress}
                isLicensed={isLicensed}
                onChooseDate={() => {
                  openAvailabilityModal({ expectedFirstDay: null });
                }}
              />

              <span className="text-sm font-medium text-neutral-lighter lg:font-medium">{cardDoctorName}</span>

              {isDomicile && <ELBadge label="A domicilio" color="primary" size="medium" Icon={HomeIcon} />}

              {isOnline && <ELBadge label="Prestazione online" color="primary" size="medium" Icon={VideoCameraIcon} />}
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col justify-between gap-4 px-4 lg:gap-7 lg:px-6 lg:pt-6">
          {!isLicensed || !specialityId || !doctorId ? (
            <NoAvailabilitiesInfo
              title="Al momento non è possibile prenotare"
              {...(showNoAvailabilitiesCallToAction && {
                description: 'Visualizza il profilo',
                path: allClinicServicesLink,
              })}
            />
          ) : (
            <>
              {isAvailabilityModalOpenInfo && (
                <AvailabilityModalV3
                  specialityId={specialityId}
                  doctors={item.specializations.doctors}
                  workgroupId={workgroupId}
                  initialSelectedDay={null}
                  firstDayExpected={isAvailabilityModalOpenInfo.expectedFirstDay}
                  onClose={() => setIsAvailabilityModalOpenInfo(null)}
                  onSubmit={handleSubmit}
                />
              )}
              <div className="flex items-start justify-between">
                <CardAvailabilityInfo firstTimeAvailable={selectedDoctor?.nextAvailability} />

                <PriceSection
                  hasDynamicPrice={hasDynamicPrice}
                  isLicensedClinic={isLicensed}
                  price={price}
                  hasAvailableSlots={hasAvailableSlots}
                />
              </div>

              <BookingActions
                hasAvailableSlots={hasAvailableSlots}
                onClick={() =>
                  openAvailabilityModal({
                    expectedFirstDay: selectedDoctor?.nextAvailability ? getStartOfDayTime(selectedDoctor.nextAvailability) : null,
                  })
                }
                onNoAvailabilitiesClick={() => setShowAllServicesModal(true)}
                showPhoneNumber={showPhoneNumber && !isWelfare}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardV3;
